import { useEffect } from "react";
import { z } from "zod";
import { ErrorButton } from "../../../../../../components/Button/ErrorButton.tsx";
import { SelectionDropdown } from "../../../../../../components/Dropdown/SelectionDropdown.tsx";
import { Form } from "../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../components/Form/FormContext.ts";
import { useDebugMode } from "../../../../../components/DebugMode/hooks/useDebugMode.ts";
import { PanelLayout } from "../../../../components/Layout/PanelLayout.tsx";
import { PanelSection } from "../../../../components/Panel/PanelSection.tsx";
import { ActionsBar } from "../../../../components/Panel/Settings/ActionsBar.tsx";
import { ModelSelectionSection } from "../../../../components/Panel/Settings/ModelSelectionSection.tsx";
import { PromptSection } from "../../../../components/Panel/Settings/PromptSection.tsx";
import { SwitchSubsection } from "../../../../components/Panel/SwitchSubsection.tsx";
import { WrappedPanelSection } from "../../../../components/Panel/WrappedPanelSection.tsx";
import { GENERATION_RATIOS } from "../../../../constants.ts";
import { GENERATION_RATIO_DISPLAY } from "../../../../display.ts";
import { useBoardUuid } from "../../../../hooks/useBoardUuid.ts";
import type { RatioDisplay } from "../../../../types.ts";
import { getErrorsFiltered } from "../../../../utils/getErrorsFiltered.ts";
import { usePromptWarnings } from "../../../hooks/usePromptWarnings.ts";
import { scrollCreateWorkspaceGridToTop } from "../../utils/scrollCreateWorkspaceGridToTop.ts";
import { useBriefSettings } from "./hooks/useBriefSettings.ts";
import { useGenerateImagesFromBrief } from "./hooks/useGenerateImagesFromBrief.ts";

const BRIEF_GENERATION_RATIOS = ["mix", ...GENERATION_RATIOS] as const;

type BriefGenerationRatio = (typeof BRIEF_GENERATION_RATIOS)[number];

export type BriefRatioDisplay = Omit<RatioDisplay, "iconName"> & {
  iconName: RatioDisplay["iconName"] | "Proportions";
};

const BRIEF_GENERATION_RATIO_DISPLAY: Record<
  BriefGenerationRatio,
  BriefRatioDisplay
> = {
  mix: {
    label: "Mix",
    iconName: "Proportions",
  },
  ...GENERATION_RATIO_DISPLAY,
};

export const BriefSettings = () => {
  const { mutation: generateFromBrief, isLoading } = useGenerateImagesFromBrief(
    {
      onSuccess: scrollCreateWorkspaceGridToTop,
    },
  );

  return (
    <Form
      schema={zBriefSettingsValidationSchema}
      initialValues={{
        prompt: "",
        areSelectedModelsValid: false,
      }}
      onSubmit={() => {
        // FIXME: make the mutation async
        generateFromBrief();
        return Promise.resolve(true);
      }}
    >
      <BriefSettingsFormContent isGenerationLoading={isLoading} />
    </Form>
  );
};

const zBriefSettingsValidationSchema = z.object({
  areSelectedModelsValid: z.boolean().refine((val) => val, {
    message:
      "The selected models are not valid, please try again by reseting the selection.",
  }),
  prompt: z.string().min(1, { message: "Please enter a brief" }),
});
type BriefSettingsValidationSchemaValues = z.input<
  typeof zBriefSettingsValidationSchema
>;

const BriefSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const boardUuid = useBoardUuid();

  const { tool, settings, setSettings, areSelectedModelsValid, modelActions } =
    useBriefSettings(boardUuid);

  const { setValues, submit, useError } =
    useForm<BriefSettingsValidationSchemaValues>();
  const modelsNotValidError = useError((v) => v.areSelectedModelsValid);
  const promptEmptyError = useError((v) => v.prompt);

  useEffect(() => {
    setValues({
      areSelectedModelsValid,
    });
  }, [setValues, areSelectedModelsValid]);

  useEffect(() => {
    setValues({ prompt: settings.prompt });
  }, [setValues, settings.prompt]);

  const isDebugModeOn = useDebugMode();

  const warnings = usePromptWarnings({
    prompt: settings.prompt,
    selectedModels: settings.selectedModels.models,
  });

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Generate images"
          intercomArticle="generate_from_a_brief"
          errors={getErrorsFiltered([modelsNotValidError])}
          errorsChildren={
            modelsNotValidError && (
              <ErrorButton
                onClick={() => {
                  modelActions.reset();
                }}
              >
                Reset model selection
              </ErrorButton>
            )
          }
        />
      }
    >
      <PromptSection
        sectionTitle="Campaign brief"
        prompt={settings.prompt}
        warnings={warnings}
        setPrompt={(newPrompt) =>
          setSettings({
            prompt: newPrompt,
          })
        }
        placeholder="Describe your campaign goals and periodicity, feel free to add any relevant context"
        autoFocus
        onEnterKeyDown={submit}
        errors={getErrorsFiltered([promptEmptyError])}
        className="min-h-[140px]"
      />
      <ModelSelectionSection tool={tool} />
      <PanelSection
        title="Ratio"
        rightItem={
          <SelectionDropdown
            value={settings.generation_ratio ?? "mix"}
            options={BRIEF_GENERATION_RATIOS.map((preset) => ({
              value: preset,
              ...BRIEF_GENERATION_RATIO_DISPLAY[preset],
            }))}
            onChange={(generationRatio) => {
              setSettings({
                generation_ratio:
                  generationRatio === "mix" ? null : generationRatio,
              });
            }}
            size="sm"
            align="end"
            buttonClassName="w-[94px]"
          />
        }
      />
      {isDebugModeOn && (
        <WrappedPanelSection title="Debug parameters">
          <div className="flex-col gap-150">
            <SwitchSubsection
              switchName="Enforce photorealistic effects"
              value={settings.enforce_photo}
              onChange={(enforcePhoto) => {
                setSettings({
                  enforce_photo: enforcePhoto,
                });
              }}
            />
            {settings.enforce_photo && (
              <SwitchSubsection
                switchName="Add photorealistic effects"
                value={settings.photo_enabled}
                onChange={(photoEnabled) => {
                  setSettings({
                    photo_enabled: photoEnabled,
                  });
                }}
              />
            )}
          </div>
        </WrappedPanelSection>
      )}
    </PanelLayout>
  );
};
